import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import LabeledFrame from 'components/LabeledFrame';
import { formatNumber } from 'helpers/generic';
import { getFromSession } from 'helpers/session';

const Table = styled.table`
	font-size: 12px;

	th {
		font-weight: 600;
	}

	td,
	th {
		&:not(:first-of-type) {
			text-align: right;
			width: 100px;
		}

		position: relative;

		&.initial {
			font-weight: 500;
		}
	}

	input {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		border: 0;
		text-align: right;
		padding-right: 5px;
		outline: none;
		border-radius: 0;

		&.with-errors {
			background: var(--bs-red);
			color: white;
		}

		&:disabled {
			background: var(--bs-gray-200);
			cursor: not-allowed;
		}
	}
`;

const StateNote = styled.div`
	font-size: 11px;
	color: var(--bs-gray-700);
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 6px;

	.spinner-border {
		width: 0.9rem;
		height: 0.9rem;
		border-width: 0.15em;
	}
`;

let axiosCancelToken = null;

export default function Balances(props) {
	let propertyId = props.propertyId ?? null;
	let communityId = props.communityId ?? null;
	let initialBalanceReceipts = props.initialBalanceReceipts ?? 0;
	let initialBalanceExpenses = props.initialBalanceExpenses ?? 0;
	let updateParentInitialBalanceReceipts = props.updateParentInitialBalanceReceipts;
	let updateParentInitialBalanceExpenses = props.updateParentInitialBalanceExpenses;
	let initialBalanceError = props.initialBalanceError ?? null;
	let readOnly = props.readOnly ?? false;
	let forceReloadParent = props.forceReloadParent ?? false;
	// Plan 2.2: parent passes the year currently selected in the navbar so reads
	// of historical/current balances stay consistent with the rest of the UI.
	let selectedYear = props.selectedYear ?? null;

	const [balances, setBalances] = useState(null);
	// Full FY object {year, status, current_operation, ...} for the selected year.
	// Null while loading; { __missing: true } if no FY exists for that year.
	const [yearState, setYearState] = useState(null);

	// Polling interval id, only set while there's an active async operation.
	const pollIntervalRef = useRef(null);

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();
		return function cleanup() {
			axiosCancelToken.cancel();
			if ( pollIntervalRef.current ) clearInterval(pollIntervalRef.current);
		}
	}, []);

	// Load property's running balances (independent of selected year — these are
	// the live debit/credit totals shown above the editable initial balance row).
	useEffect(() => {
		if ( !propertyId ) return;
		axios.get('/api/manager/properties/get-balances/' + propertyId, {
			params: { community_id: communityId },
			cancelToken: axiosCancelToken.token,
		}).then((response) => {
			setBalances(response.data);
		}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});
	}, [propertyId, communityId, forceReloadParent]);

	// Resolve which year to read/edit. Prop > sessionStorage > undefined.
	const resolvedYear = selectedYear ?? (() => {
		const stored = getFromSession('navbar-fiscalyear');
		return stored != null ? Number(stored) : null;
	})();

	// Fetch the state of that specific year (status + current_operation).
	const fetchYearState = async () => {
		if ( !resolvedYear ) {
			setYearState({ __missing: true });
			return;
		}
		try {
			const response = await axios.get(
				'/api/manager/financial-years/get-by-year/' + resolvedYear,
				{
					params: { community_id: communityId },
					cancelToken: axiosCancelToken.token,
				}
			);
			setYearState(response.data);
		} catch (error) {
			if ( axios.isCancel(error) ) return;
			if ( error?.response?.status === 404 ) {
				setYearState({ __missing: true });
			}
		}
	};

	useEffect(() => {
		fetchYearState();
		// Listen for navbar fiscal year changes — parent already triggers a
		// property reload via forceReloadParent, but we re-fetch year-state too.
		const handler = () => fetchYearState();
		document.addEventListener('fiscalYearChanged', handler);
		return function cleanup() {
			document.removeEventListener('fiscalYearChanged', handler);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resolvedYear, communityId, forceReloadParent]);

	// Polling: while the year has an async operation in flight, re-check every 5s.
	useEffect(() => {
		const op = yearState?.current_operation ?? null;
		if ( pollIntervalRef.current ) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}
		if ( op ) {
			pollIntervalRef.current = setInterval(fetchYearState, 5000);
		}
		return () => {
			if ( pollIntervalRef.current ) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [yearState?.current_operation]);

	const yearMissing = yearState?.__missing === true;
	const yearClosed = yearState?.status === 'closed';
	const yearOperation = yearState?.current_operation ?? null;
	const inputsDisabled = readOnly || yearMissing || yearClosed || !!yearOperation;

	let disabledReason = null;
	if ( yearMissing ) disabledReason = 'No existe ejercicio fiscal para ese año.';
	else if ( yearClosed ) disabledReason = 'Ejercicio fiscal cerrado: no se pueden modificar los saldos iniciales.';
	// While an async operation is in flight we still disable the inputs but
	// we don't surface a textual reason — the user gets the visual lock and
	// the proper toast/spinner from the FY screen.

	return (
		<LabeledFrame
			className="mb-3"
			label="Saldos"
		>
			<Table className="table table-sm table-bordered mb-0">
				<thead>
					<tr>
						<th></th>
						<th>Saldo</th>
					</tr>
				</thead>
				<tbody>
					{ balances !== null &&
						<React.Fragment>
							<tr>
								<td>Saldo de recibos</td>
								<td className={'text-' + (balances.receipts.balance >= 0  ? 'success' : 'danger')}>{formatNumber(balances.receipts.balance)}</td>
							</tr>
							<tr>
								<td>Gasto generado de propiedades</td>
								<td className={'text-' + (balances.expenses.balance >= 0 ? 'success' : 'danger')}>{formatNumber(balances.expenses.balance)}</td>
							</tr>
						</React.Fragment>
					}
				</tbody>
				{ resolvedYear &&
					<tfoot>
						<tr>
							<td className="initial">Saldo inicial de recibos en {resolvedYear}</td>
							<td>
								<input
									type="number"
									step="0.01"
									className={'form-control no-arrows ' + (initialBalanceError ? 'with-errors' : '')}
									onChange={(e) => updateParentInitialBalanceReceipts(e.target.value ?? 0)}
									value={initialBalanceReceipts}
									readOnly={inputsDisabled}
									disabled={inputsDisabled}
									title={disabledReason ?? ''}
								/>
							</td>
						</tr>
						<tr>
							<td className="initial">Saldo inicial de gastos en {resolvedYear}</td>
							<td>
								<input
									type="number"
									step="0.01"
									className={'form-control no-arrows ' + (initialBalanceError ? 'with-errors' : '')}
									onChange={(e) => updateParentInitialBalanceExpenses(e.target.value ?? 0)}
									value={initialBalanceExpenses}
									readOnly={inputsDisabled}
									disabled={inputsDisabled}
									title={disabledReason ?? ''}
								/>
							</td>
						</tr>
					</tfoot>
				}
			</Table>
			{ disabledReason &&
				<StateNote>
					{ yearOperation && <span className="spinner-border" role="status" aria-hidden="true"></span> }
					<span>{disabledReason}</span>
				</StateNote>
			}
		</LabeledFrame>
	);
}
